import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import * as turf from "@turf/turf";
import moment from "moment-timezone";
import proj4 from "proj4";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import Mainloader from "../../common/Mainloader";
import { MapHomeControl, MapInfoControl } from "../../common/MapControls/MapControls";
import PolygonDownloadModal from "./PolygonDownloadModal";
import { moduleByKey } from "../../Homepage/AdminPanel/ReportApproval/reviewableModules";
import {
    Car,
    ChevronLeft,
    ChevronRight,
    CircleDashed,
    CloudRain,
    Download,
    Flame,
    Layers,
    Mountain,
    Pentagon,
    Trash2,
    Waves,
    Wind,
    X,
    Zap,
} from "lucide-react";
import "./ActiveEventsHub.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_API_URL_LOCAL || "";
const MAP_CENTER = [-8.85, 125.25];
const DEFAULT_ZOOM = 8.0;

// ---------------------------------------------------------------------------
// Shared hazard identity (icon/color), matching the palette already used on
// the Active Events panel (Dashboard/Activeevents/ActiveDisaster.js) so a
// hazard reads as the same color everywhere in the app.
// ---------------------------------------------------------------------------
// gifUrl: the same animated legend GIFs already used for the Fire/Landslide
// forecast layer (public/Dashboard/Legends/) - reused here so live events of
// that hazard type get the same animated marker. Lightning has no gifUrl
// (no lightning.gif asset exists yet) and no LineString/point volume here
// can reach thousands of markers at once, so it keeps its lightweight
// circleMarker/dot rendering rather than switching to real DOM <img> markers.
const EVENT_TYPE_META = {
    cyclone: { label: "Cyclone", color: "#0d6efd", Icon: Wind, gifUrl: "/Dashboard/Legends/cyclone.gif" },
    landslide: { label: "Landslide", color: "#6f42c1", Icon: Mountain, gifUrl: "/Dashboard/Legends/landslide.gif" },
    earthquake: { label: "Earthquake", color: "#fd7e14", Icon: Waves, gifUrl: "/Dashboard/Legends/earthquake.gif" },
    flood: { label: "Flood", color: "#20c997", Icon: CloudRain, gifUrl: "/Dashboard/Legends/flood.gif" },
    fire: { label: "Fire Risk", color: "#dc3545", Icon: Flame, gifUrl: "/Dashboard/Legends/fire.gif" },
    lightning: { label: "Lightning", color: "#e5a300", Icon: Zap, gifUrl: null },
    roadAccident: { label: "Road Accident", color: "#6c757d", Icon: Car, gifUrl: "/Dashboard/Legends/roadaccident.gif" },
};
// Same code_type lookup as Homepage/Earthquake/HealthCenters.js, used here to
// turn the hospital API's numeric code_type into a readable facility name
// for the polygon download.
const HOSPITAL_CODE_TYPE_MAP = {
    1: { name: "Community Health Center" },
    2: { name: "Community Health Center (Internment)" },
    3: { name: "Health Post" },
    4: { name: "Referral Hospital" },
    5: { name: "Regional Hospital" },
    6: { name: "National Hospital" },
    7: { name: "Clinic" },
    8: { name: "Clinic and Pharmacy" },
    9: { name: "Pharmacy" },
    10: { name: "Disability Rehabilitation Center" },
    11: { name: "Multi Drug Rehabilitation Center" },
    12: { name: "Mental Rehabilitation Center" },
};

const API_EVENT_LABEL_TO_TYPE = {
    Cyclone: "cyclone",
    Landslide: "landslide",
    Earthquake: "earthquake",
    Flood: "flood",
    "Fire Risk": "fire",
    Lightning: "lightning",
    "Road Accident": "roadAccident",
};

// This page's own `type` key -> reviewableModules.js's `key`, so a merged
// group's Make Active/Unmerge buttons hit the right hazard's endpoint
// (.base already carries the full felt-report list URL for that module).
const TYPE_TO_REVIEWABLE_KEY = {
    cyclone: "cyclone",
    landslide: "landslide",
    earthquake: "earthquake",
    flood: "flood",
    fire: "firerisk",
    lightning: "lightning",
    roadAccident: "road_accident",
};

// Same role check Sidebar.js uses for isSuperAdmin - Active Events treats
// plain Admin the same as Super Admin here (matches the backend's
// is_privileged(), which both promote/unmerge and the pending-visibility
// rule are gated on).
const isPrivilegedViewer = () => {
    const role = localStorage.getItem("role");
    return role === "admin" || role === "superadmin" || localStorage.getItem("is_superuser") === "true";
};

// ---------------------------------------------------------------------------
// Small utilities
// ---------------------------------------------------------------------------
const safeFetchJson = async (url, options) => {
    try {
        const res = await fetch(url, options);
        if (!res.ok) return null;
        return await res.json();
    } catch (e) {
        console.error("Fetch failed:", url, e);
        return null;
    }
};

// common/active-events/ resolves Admin/Super Admin visibility (pending
// reports) from request.user server-side — without this header the backend
// sees an anonymous caller no matter who's actually logged in, and silently
// falls back to verified-only for everyone, admins included.
const authHeaders = () => {
    const token = localStorage.getItem("access_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
};

// Groups events within `radiusM` metres of each other — union-find over
// pairwise Haversine distance, so a chain of nearby reports clusters
// together even if the two ends of the chain are further apart than
// radiusM from each other directly. Only point events (lat/lon both set)
// are eligible; a singleton "cluster" isn't returned as a buffer. Runs
// entirely client-side — the backend has no clustering logic at all.
const buildEventBuffers = (events, radiusM = 100) => {
    const haversineM = (lat1, lon1, lat2, lon2) => {
        const R = 6371000;
        const toRad = (d) => (d * Math.PI) / 180;
        const dPhi = toRad(lat2 - lat1);
        const dLambda = toRad(lon2 - lon1);
        const a =
            Math.sin(dPhi / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLambda / 2) ** 2;
        return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };

    const pointIdx = events.reduce((acc, e, i) => {
        if (e.lat !== null && e.lat !== undefined && e.lon !== null && e.lon !== undefined) acc.push(i);
        return acc;
    }, []);

    const parent = new Map(pointIdx.map((i) => [i, i]));
    const find = (i) => {
        while (parent.get(i) !== i) {
            parent.set(i, parent.get(parent.get(i)));
            i = parent.get(i);
        }
        return i;
    };
    const union = (a, b) => {
        const ra = find(a);
        const rb = find(b);
        if (ra !== rb) parent.set(rb, ra);
    };

    for (let a = 0; a < pointIdx.length; a++) {
        for (let b = a + 1; b < pointIdx.length; b++) {
            const i = pointIdx[a];
            const j = pointIdx[b];
            if (haversineM(events[i].lat, events[i].lon, events[j].lat, events[j].lon) <= radiusM) union(i, j);
        }
    }

    const groups = new Map();
    pointIdx.forEach((i) => {
        const root = find(i);
        if (!groups.has(root)) groups.set(root, []);
        groups.get(root).push(i);
    });

    const buffers = [];
    let seq = 0;
    groups.forEach((members) => {
        if (members.length < 2) return;
        seq += 1;
        const centerLat = members.reduce((s, i) => s + events[i].lat, 0) / members.length;
        const centerLon = members.reduce((s, i) => s + events[i].lon, 0) / members.length;
        const coveringRadius = Math.max(
            ...members.map((i) => haversineM(centerLat, centerLon, events[i].lat, events[i].lon))
        );
        const bufferId = `buffer:${seq}`;
        const moduleSet = new Set();
        let hasPending = false;
        members.forEach((i) => {
            events[i].bufferId = bufferId;
            moduleSet.add(EVENT_TYPE_META[events[i].type]?.label || events[i].type);
            if (events[i].verificationStatus === "pending") hasPending = true;
        });
        buffers.push({
            id: bufferId,
            center: { lat: centerLat, lon: centerLon },
            radiusM: Math.max(coveringRadius, 25),
            reportCount: members.length,
            eventIds: members.map((i) => events[i].id),
            modules: Array.from(moduleSet).sort(),
            hasPending,
        });
    });

    return buffers;
};

// Timor-Leste is UTC+9, no DST — matches getForecastDate(0) in
// Homepage/Landslide/Forecast/dateUtils.js, the real page's "today" logic.
const todayDiliStr = () => {
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Dili", year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(new Date());
    const get = (t) => parts.find((p) => p.type === t)?.value;
    return `${get("year")}-${get("month")}-${get("day")}`;
};

// Lightning nowcast slot logic — copied verbatim from the real production
// page (Homepage/Lightning/Nowcast/Nowcast.js: TL_OFFSET_HOURS/getTLNow/
// floorTo5Min/formatSlotForApi) so the datetime param and 5-min alignment
// match exactly. The `+9h then read UTC getters` trick is intentional: it
// makes a Date object whose UTC* fields directly are Dili local time.
const TL_OFFSET_HOURS = 9;
const getTLNow = () => new Date(Date.now() + TL_OFFSET_HOURS * 3600000);
const floorTo5Min = (d) => {
    const m = Math.floor(d.getUTCMinutes() / 5) * 5;
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours(), m, 0, 0));
};
const formatSlotForApi = (d) => {
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}/${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
};

// Same 5 recency buckets + colors the real Nowcast page uses (BUCKET_CONFIG).
const LIGHTNING_BUCKET_META = {
    "5mins": { color: "#ef4444", label: "Last 5 min" },
    "10-5min": { color: "#f97316", label: "10–5 min ago" },
    "15-10min": { color: "#ffc003", label: "15–10 min ago" },
    "30-15min": { color: "#fde047", label: "30–15 min ago" },
    "24hours": { color: "#9ca3af", label: "Past 24 hrs" },
};

const earthquakeColor = (mag) => {
    if (mag >= 6) return "#dc2626";
    if (mag >= 5) return "#f97316";
    if (mag >= 4) return "#eab308";
    return "#22c55e";
};

// Copied verbatim from Homepage/Landslide/Forecast/Forecast.js (getLandslideCharacteristic)
// so "High" here means exactly what the real Landslide Forecast page calls High.
const classifyLandslideAdvisory = (advisory) => {
    if (!advisory) return "NoRisk";
    const t = advisory.toLowerCase();
    if (t.includes("high")) return "High";
    if (t.includes("medium")) return "Medium";
    if (t.includes("low")) return "Low";
    if (t.includes("no")) return "NoRisk";
    return "NoRisk";
};

// Copied verbatim from Homepage/FireRisk/Forecast/Forecast.js (mapAdvisoryToRiskLevel).
const classifyFireAdvisory = (advisory) => {
    if (!advisory) return "N/A";
    const t = advisory.trim().toLowerCase();
    if (t.includes("extreme")) return "Extreme Risk";
    if (t.includes("high")) return "High Risk";
    if (t.includes("moderate")) return "Moderate Risk";
    if (t.includes("low")) return "Low Risk";
    return "N/A";
};

// Animated GIF marker — reuses the app's own existing legend animations
// (public/Dashboard/Legends/) instead of a static icon. `opacity` lets
// callers dim a marker for a pending (not yet verified) report, the same
// visual cue the plain circleMarkers already used before switching to icons.
const buildAnimatedIcon = (gifUrl, size = 38, opacity = 1) => {
    return L.divIcon({
        className: "aeh-forecast-icon",
        html: `<img src="${gifUrl}" alt="" style="width:${size}px;height:${size}px;display:block;filter:drop-shadow(0 1px 3px rgba(0,0,0,0.5));mix-blend-mode:multiply;opacity:${opacity};" />`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
    });
};

// ---------------------------------------------------------------------------
// Map child components (imperative Leaflet layers — same pattern already
// used in Activeevents/ActiveDisaster.js for full styling control).
// ---------------------------------------------------------------------------
const AdmContextOutline = ({ geo, weight = 2 }) => {
    const map = useMap();
    useEffect(() => {
        if (!geo) return undefined;
        const layer = L.geoJSON(geo, { style: { color: "#000000", weight, fillOpacity: 0, opacity: 0.75 } }).addTo(map);
        return () => map.removeLayer(layer);
    }, [map, geo, weight]);
    return null;
};

// A merged report's canonical/active copy is the only one that ever
// reaches this feed (see common/views.py ActiveEventsView docstring) - its
// own duplicates carry verification_status 'merged' and never arrive as
// separate events. Acting on one (Make Active / Unmerge) always posts to
// the felt-report endpoint for the module the report actually belongs to.
const postReportAction = async (moduleKey, reportId, action, onDone) => {
    const mod = moduleByKey(TYPE_TO_REVIEWABLE_KEY[moduleKey]);
    if (!mod) return;
    const token = localStorage.getItem("access_token");
    try {
        const res = await fetch(`${mod.base}${reportId}/${action}/`, {
            method: "POST",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) onDone();
    } catch (err) {
        console.error(`Failed to ${action} report #${reportId}:`, err);
    }
};

// Builds the popup DOM for a canonical report - a plain HTML string can't
// carry the Make Active/Unmerge click handlers merged-member rows need, so
// this is a real element instead of bindPopup(htmlString) like every other
// layer on this page.
const buildEventPopupEl = (e, meta, pending, isPrivilegedUser) => {
    const el = document.createElement("div");
    el.style.minWidth = "200px";
    el.style.fontSize = "13px";

    const title = document.createElement("div");
    title.innerHTML = `<strong>${meta.label}</strong>${pending ? " <em>(pending verification)</em>" : ""}`;
    el.appendChild(title);

    const loc = document.createElement("div");
    loc.textContent = e.locationName;
    el.appendChild(loc);

    if (e.advisory) {
        const adv = document.createElement("div");
        adv.textContent = `Advisory: ${e.advisory}`;
        el.appendChild(adv);
    }

    if (e.reportGroupCount > 1) {
        const note = document.createElement("div");
        note.style.marginTop = "6px";
        note.style.fontStyle = "italic";
        note.textContent = isPrivilegedUser
            ? `${e.reportGroupCount} reports total — the other ${e.reportGroupCount - 1} are pinned nearby (smaller markers); open one to reassign or unmerge it.`
            : `${e.reportGroupCount - 1} other user${e.reportGroupCount - 1 === 1 ? "" : "s"} reported this event`;
        el.appendChild(note);
    }

    return el;
};

// One of these per merged-duplicate member, only ever rendered for a
// privileged viewer (members[] is empty for everyone else - the backend
// never sends it to a non-privileged caller). Smaller/subdued marker, own
// popup with the two group-editing actions Report Approval also offers.
const buildMemberPopupEl = (member, moduleKey, reportId, onDone) => {
    const el = document.createElement("div");
    el.style.minWidth = "170px";
    el.style.fontSize = "13px";

    const title = document.createElement("div");
    title.innerHTML = `<strong>Merged report</strong>`;
    el.appendChild(title);

    const sub = document.createElement("div");
    sub.style.marginBottom = "6px";
    sub.textContent = `Currently folded into #${reportId}`;
    el.appendChild(sub);

    const row = document.createElement("div");
    row.style.display = "flex";
    row.style.gap = "6px";

    const activeBtn = document.createElement("button");
    activeBtn.textContent = "Make Active";
    activeBtn.style.cssText = "font-size:11px;cursor:pointer;padding:3px 8px;border-radius:6px;border:1px solid #2563eb;background:#eff6ff;color:#2563eb;";
    activeBtn.onclick = () => postReportAction(moduleKey, member.id.split(":")[1] || member.id, "promote", onDone);
    row.appendChild(activeBtn);

    const unmergeBtn = document.createElement("button");
    unmergeBtn.textContent = "Unmerge";
    unmergeBtn.style.cssText = "font-size:11px;cursor:pointer;padding:3px 8px;border-radius:6px;border:1px solid #ad4141;background:#fdf2f2;color:#ad4141;";
    unmergeBtn.onclick = () => postReportAction(moduleKey, member.id.split(":")[1] || member.id, "unmerge", onDone);
    row.appendChild(unmergeBtn);

    el.appendChild(row);
    return el;
};

// Reports within 100m of each other are grouped client-side (`e.bufferId`)
// and, above this zoom, shown as one pulsing buffer marker instead of
// separate overlapping pins. At or past it (zoomed in to street level) the
// 100m separation is visually meaningful again, so each report gets its
// own pin and the buffer marker for that group disappears.
const BUFFER_DEEP_ZOOM = 15;

// A pulsing marker (same visual language as the Home dashboard's Active
// Events widget - Dashboard/Activeevents/ActiveDisaster.js's buildPulsingIcon)
// instead of a plain circle: catches the eye at a glance instead of reading
// as just another static pin, and the report count sits right on it so the
// group's size is legible without opening the popup. Sized in screen
// pixels (not real-world metres - 100m is only a few px at most working
// zooms) and kept deliberately modest so it doesn't dominate the map.
const bufferMarkerSize = (zoom, reportCount) => {
    const zoomBonus = Math.max(0, zoom - 7) * 1.1;
    const countBonus = Math.min(6, Math.max(0, reportCount - 2));
    return Math.min(26, Math.max(14, 12 + zoomBonus + countBonus));
};

const ensurePulseAnimationCss = () => {
    if (document.getElementById("pulse-animation")) return;
    const style = document.createElement("style");
    style.id = "pulse-animation";
    style.innerHTML = `
        @keyframes pulse-ring {
            0% { transform: scale(0.5); opacity: 0.6; }
            80%, 100% { transform: scale(2.2); opacity: 0; }
        }`;
    document.head.appendChild(style);
};

const buildBufferPulseIcon = (b, zoom) => {
    const size = bufferMarkerSize(zoom, b.reportCount);
    const color = b.hasPending ? "#f59e0b" : "#dc3545";
    return L.divIcon({
        className: "aeh-buffer-pulse",
        html: `
            <div style="position: relative; width: ${size}px; height: ${size}px;">
                <div style="
                    width: ${size}px; height: ${size}px;
                    background-color: ${color};
                    border: 2px solid #fff;
                    border-radius: 50%;
                    position: absolute; top: 0; left: 0;
                    display: flex; align-items: center; justify-content: center;
                    color: #fff; font-weight: 700; font-size: ${Math.max(9, size * 0.42)}px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.4);
                ">${b.reportCount}</div>
                <div style="
                    width: ${size}px; height: ${size}px;
                    background-color: ${color};
                    border-radius: 50%;
                    position: absolute; top: 0; left: 0;
                    opacity: 0.45;
                    animation: pulse-ring 1.5s infinite;
                "></div>
            </div>
        `,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
    });
};

const ActiveEventsMarkersLayer = ({ events, buffers, onRefresh }) => {
    const map = useMap();
    const [zoom, setZoom] = useState(() => map.getZoom());

    useEffect(() => {
        const handleZoom = () => setZoom(map.getZoom());
        map.on("zoomend", handleZoom);
        return () => map.off("zoomend", handleZoom);
    }, [map]);

    useEffect(() => {
        const group = L.layerGroup().addTo(map);
        const isPrivilegedUser = isPrivilegedViewer();
        const deepZoom = zoom >= BUFFER_DEEP_ZOOM;

        const hasPoint = (e) => e.lat !== null && e.lat !== undefined && e.lon !== null && e.lon !== undefined;
        const toLatLng = ([lon, lat]) => [lat, lon]; // GeoJSON is [lon, lat]; Leaflet wants [lat, lon]

        events.forEach((e) => {
            const meta = EVENT_TYPE_META[e.type] || EVENT_TYPE_META.roadAccident;
            const pending = e.verificationStatus === "pending";

            if (hasPoint(e)) {
                if (e.bufferId && !deepZoom) return; // covered by its buffer pulse marker instead
                const marker = meta.gifUrl
                    ? L.marker([e.lat, e.lon], { icon: buildAnimatedIcon(meta.gifUrl, 30, pending ? 0.6 : 1) }).addTo(group)
                    : L.circleMarker([e.lat, e.lon], {
                        radius: 8, color: "#fff", weight: 2, fillColor: meta.color,
                        fillOpacity: pending ? 0.55 : 0.95, dashArray: pending ? "3,3" : null,
                    }).addTo(group);
                marker.bindPopup(buildEventPopupEl(e, meta, pending, isPrivilegedUser));
            } else if (e.geometry?.type === "Polygon") {
                // A hand-drawn affected area - lat/lon are null for these by
                // design, never eligible for 100m buffering (only point
                // reports are), so no bufferId check needed here.
                //
                // Below deepZoom: the shape is easy to draw at street-level
                // zoom but can be genuinely a handful of screen pixels (or
                // less) at the map's default country-wide zoom, so the
                // polygon outline itself stays hidden and this just reads as
                // a normal point event - the same animated icon (same size)
                // a point report of this hazard would show, at its centroid.
                // At/above deepZoom the shape IS legible, so the polygon
                // outline appears (plus a white ring circumscribing its
                // extent), with that same icon now framed at its center.
                try {
                    const [centroidLon, centroidLat] = turf.centroid(e.geometry).geometry.coordinates;
                    if (deepZoom) {
                        const rings = e.geometry.coordinates.map((ring) => ring.map(toLatLng));
                        const polygon = L.polygon(rings, {
                            color: meta.color, weight: pending ? 2 : 3, opacity: pending ? 0.6 : 0.9,
                            dashArray: pending ? "4,4" : null, fillColor: meta.color, fillOpacity: pending ? 0.15 : 0.25,
                        }).addTo(group);
                        polygon.bindPopup(buildEventPopupEl(e, meta, pending, isPrivilegedUser));

                        const outerRing = e.geometry.coordinates[0] || [];
                        const coveringRadiusM = outerRing.reduce((max, [vLon, vLat]) => {
                            const d = turf.distance([centroidLon, centroidLat], [vLon, vLat], { units: "meters" });
                            return Math.max(max, d);
                        }, 0);
                        const ring = L.circle([centroidLat, centroidLon], {
                            radius: Math.max(coveringRadiusM * 1.15, 20), // 15% pad so it frames the shape, not hugs it
                            color: "#fff", weight: 2, opacity: pending ? 0.6 : 0.9,
                            dashArray: pending ? "3,3" : null, fillOpacity: 0,
                        }).addTo(group);
                        ring.bindPopup(buildEventPopupEl(e, meta, pending, isPrivilegedUser));

                        const centroidIcon = meta.gifUrl
                            ? L.marker([centroidLat, centroidLon], { icon: buildAnimatedIcon(meta.gifUrl, 30, pending ? 0.6 : 1) }).addTo(group)
                            : L.circleMarker([centroidLat, centroidLon], {
                                radius: 8, color: "#fff", weight: 2, fillColor: meta.color,
                                fillOpacity: pending ? 0.55 : 0.95, dashArray: pending ? "3,3" : null,
                            }).addTo(group);
                        centroidIcon.bindPopup(buildEventPopupEl(e, meta, pending, isPrivilegedUser));
                    } else {
                        const centroidMarker = meta.gifUrl
                            ? L.marker([centroidLat, centroidLon], { icon: buildAnimatedIcon(meta.gifUrl, 30, pending ? 0.6 : 1) }).addTo(group)
                            : L.circleMarker([centroidLat, centroidLon], {
                                radius: 8, color: "#fff", weight: 2, fillColor: meta.color,
                                fillOpacity: pending ? 0.55 : 0.95, dashArray: pending ? "3,3" : null,
                            }).addTo(group);
                        centroidMarker.bindPopup(buildEventPopupEl(e, meta, pending, isPrivilegedUser));
                    }
                } catch (err) {
                    console.error("Could not compute centroid for polygon event:", e.id, err);
                }
            } else if (e.geometry?.type === "LineString") {
                // A reported stretch (start point -> end point).
                const latlngs = e.geometry.coordinates.map(toLatLng);
                const line = L.polyline(latlngs, {
                    color: meta.color, weight: pending ? 3 : 4, opacity: pending ? 0.6 : 0.9,
                    dashArray: pending ? "4,4" : "6,8",
                }).addTo(group);
                line.bindPopup(buildEventPopupEl(e, meta, pending, isPrivilegedUser));
                latlngs.forEach((latlng) => {
                    L.circleMarker(latlng, { radius: 4, color: "#fff", weight: 1.5, fillColor: meta.color, fillOpacity: 0.9 })
                        .addTo(group)
                        .bindPopup(buildEventPopupEl(e, meta, pending, isPrivilegedUser));
                });
            }

            if (isPrivilegedUser && e.reportGroupCount > 1) {
                (e.members || []).forEach((member) => {
                    if (member.lat === null || member.lat === undefined || member.lon === null || member.lon === undefined) return;
                    const memberMarker = L.circleMarker([member.lat, member.lon], {
                        radius: 5, color: "#fff", weight: 1.5, fillColor: "#7c3aed", fillOpacity: 0.85,
                    }).addTo(group);
                    memberMarker.bindPopup(buildMemberPopupEl(member, e.type, e.id.split(":")[1] || e.id, onRefresh));
                });
            }
        });

        if (!deepZoom && (buffers || []).length > 0) {
            ensurePulseAnimationCss();
            buffers.forEach((b) => {
                const marker = L.marker([b.center.lat, b.center.lon], { icon: buildBufferPulseIcon(b, zoom) }).addTo(group);
                marker.bindPopup(
                    `<strong>Buffer Zone — ${b.reportCount} reports within 100m</strong><br/>${b.modules.join(", ")}${b.hasPending ? "<br/><em>Includes pending report(s)</em>" : ""}<br/><em>Zoom in further to see each report individually.</em>`
                );
            });
        }

        return () => map.removeLayer(group);
    }, [map, events, buffers, onRefresh, zoom]);
    return null;
};

const EarthquakeMarkersLayer = ({ quakes }) => {
    const map = useMap();
    useEffect(() => {
        const group = L.layerGroup().addTo(map);
        const gifUrl = EVENT_TYPE_META.earthquake.gifUrl;
        quakes.forEach((q) => {
            if (!q.latitude || !q.longitude) return;
            // Same magnitude-scaling cue the plain circleMarker used, now
            // sizing the animated icon instead of a circle's radius.
            const marker = gifUrl
                ? L.marker([q.latitude, q.longitude], {
                    icon: buildAnimatedIcon(gifUrl, Math.max(22, Math.min(56, 18 + Number(q.magnitude || 0) * 5))),
                }).addTo(group)
                : L.circleMarker([q.latitude, q.longitude], {
                    radius: Math.max(5, Math.min(18, 4 + Number(q.magnitude || 0) * 1.6)),
                    color: "#fff", weight: 1.5, fillColor: earthquakeColor(Number(q.magnitude || 0)), fillOpacity: 0.85,
                }).addTo(group);
            marker.bindPopup(
                `<strong>${q.region || q.place || "Earthquake"}</strong><br/>Magnitude: <b>${q.magnitude ?? "N/A"}</b><br/>Depth: ${q.depth ?? "N/A"} km`
            );
        });
        return () => map.removeLayer(group);
    }, [map, quakes]);
    return null;
};

// Colored by recency bucket (red -> orange -> yellow -> light yellow -> grey),
// matching BUCKET_CONFIG on the real Nowcast page — not by pulse_type.
const LightningMarkersLayer = ({ points }) => {
    const map = useMap();
    useEffect(() => {
        const group = L.layerGroup().addTo(map);
        points.slice(0, 3000).forEach((p) => {
            if (!p.lat || !p.lng) return;
            const bucketMeta = LIGHTNING_BUCKET_META[p.bucket] || LIGHTNING_BUCKET_META["24hours"];
            const marker = L.circleMarker([p.lat, p.lng], {
                radius: 4, weight: 0, fillColor: bucketMeta.color, fillOpacity: 0.85,
            }).addTo(group);
            marker.bindPopup(
                `<strong>Lightning Observation</strong><br/>Recency: <b>${bucketMeta.label}</b><br/>Type: <b>${p.pulse_type === "cg" ? "Cloud-to-Ground" : "Intra-Cloud"}</b><br/>Peak Amplitude: ${p.peak_amplitude ?? "N/A"} kA<br/>Sensors: ${p.number_sensors ?? "N/A"}<br/>Time: ${p.created_at || p.date_hr || "N/A"}`
            );
        });
        return () => map.removeLayer(group);
    }, [map, points]);
    return null;
};

// Fire-risk / landslide "model data" icon layers — one marker per suco that
// qualifies today (Extreme fire risk / High landslide risk), placed at the
// suco polygon's centroid, nudged apart via `offsets` when another layer
// (e.g. landslide) also has a marker on the same suco, so they never stack.
const ForecastIconLayer = ({ sucos, metaByCode, gifUrl, riskLabel, kind, offsets }) => {
    const map = useMap();
    useEffect(() => {
        const group = L.layerGroup().addTo(map);
        sucos.forEach((s) => {
            const meta = metaByCode.get(s.admCode);
            if (!meta?.latlng) return;
            const [dLat, dLon] = offsets.get(`${s.admCode}:${kind}`) || [0, 0];
            const latlng = [meta.latlng[0] + dLat, meta.latlng[1] + dLon];
            const marker = L.marker(latlng, { icon: buildAnimatedIcon(gifUrl) }).addTo(group);
            const name = s.adm3 || meta.adm3 || s.admCode;
            const municipality = [s.adm2 || meta.adm2, s.adm1 || meta.adm1].filter(Boolean).join(", ");
            marker.bindPopup(
                `<strong>${name}</strong>${municipality ? `<br/>${municipality}` : ""}<br/>Risk: <b>${riskLabel}</b><br/>${s.advisory || ""}<br/>${s.forecast_date || s.date || ""}`
            );
        });
        return () => map.removeLayer(group);
    }, [map, sucos, metaByCode, gifUrl, riskLabel, kind, offsets]);
    return null;
};

// Custom polygon-draw button — built the same way as MapHomeControl (a plain
// "leaflet-top leaflet-left" overlay, not a real L.Control) so it can be
// pinned directly below the Home button via marginTop, instead of using
// react-leaflet-draw's own EditControl toolbar (which Leaflet stacks at the
// top of that corner, above Home, with no positioning control). Drawing is
// triggered imperatively via L.Draw.Polygon — after one polygon finishes,
// clicking again). "Continuous" mode is on for as long as the button stays
// toggled: once a polygon is finished, the next one is armed immediately —
// no re-click needed — until the user clicks the button again to stop.
const PolygonDrawControl = ({ hasPolygons, onCreated, onClear }) => {
    const map = useMap();
    const featureGroupRef = useRef(null);
    const activeHandlerRef = useRef(null);
    const continuousRef = useRef(false);
    const [drawing, setDrawing] = useState(false);

    const armNextPolygon = useCallback(() => {
        // leaflet-draw's default vertex marker is 8x8 on mouse but jumps to a
        // 20x20 "touchIcon" on touch-capable devices/trackpads — forcing both
        // to the same small DivIcon keeps every placed point the same, modest size.
        const vertexIcon = new L.DivIcon({
            iconSize: new L.Point(7, 7),
            className: "leaflet-div-icon leaflet-editing-icon",
        });
        const handler = new L.Draw.Polygon(map, {
            shapeOptions: { color: "#0d6efd", weight: 2, fillOpacity: 0.15 },
            icon: vertexIcon,
            touchIcon: vertexIcon,
            showArea: false,
            allowIntersection: false,
        });
        activeHandlerRef.current = handler;
        handler.enable();
    }, [map]);

    useEffect(() => {
        if (!featureGroupRef.current) {
            featureGroupRef.current = L.featureGroup().addTo(map);
        }
        const handleCreated = (e) => {
            featureGroupRef.current.addLayer(e.layer);
            onCreated(e.layer.toGeoJSON());
            if (continuousRef.current) {
                // Straight into the next polygon — no second click needed.
                armNextPolygon();
            } else {
                setDrawing(false);
            }
        };
        map.on(L.Draw.Event.CREATED, handleCreated);
        return () => map.off(L.Draw.Event.CREATED, handleCreated);
    }, [map, onCreated, armNextPolygon]);

    const noClickRef = useCallback((node) => {
        if (node) {
            L.DomEvent.disableClickPropagation(node);
            L.DomEvent.disableScrollPropagation(node);
        }
    }, []);

    const toggleDrawing = (e) => {
        e.stopPropagation();
        if (drawing) {
            continuousRef.current = false;
            activeHandlerRef.current?.disable();
            setDrawing(false);
        } else {
            continuousRef.current = true;
            setDrawing(true);
            armNextPolygon();
        }
    };

    const clearAll = (e) => {
        e.stopPropagation();
        featureGroupRef.current?.clearLayers();
        onClear();
    };

    return (
        <div className="leaflet-top leaflet-left" style={{ pointerEvents: "none" }}>
            <div
                ref={noClickRef}
                className="leaflet-control leaflet-bar"
                style={{
                    marginTop: "120px",
                    pointerEvents: "auto",
                    background: drawing ? "#0d6efd" : "#fff",
                    cursor: "pointer",
                }}
                onClick={toggleDrawing}
                title={drawing ? "Drawing — finish a polygon and the next starts right away; click again to stop" : "Draw polygons"}
            >
                <a
                    href="#draw-polygon"
                    role="button"
                    aria-label="Draw polygon"
                    onClick={(e) => e.preventDefault()}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "30px", height: "30px", color: drawing ? "#fff" : "#333", fontSize: "15px" }}
                >
                    <Pentagon size={15} />
                </a>
            </div>
            {hasPolygons && (
                <div
                    ref={noClickRef}
                    className="leaflet-control leaflet-bar"
                    style={{ marginTop: "6px", pointerEvents: "auto", background: "#fff", cursor: "pointer" }}
                    onClick={clearAll}
                    title="Clear all polygons"
                >
                    <a
                        href="#clear-polygons"
                        role="button"
                        aria-label="Clear polygons"
                        onClick={(e) => e.preventDefault()}
                        style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "30px", height: "30px", color: "#dc3545", fontSize: "15px" }}
                    >
                        <Trash2 size={15} />
                    </a>
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const ActiveEventsHub = () => {
    const [basemap, setBasemap] = useState("hybrid"); // 'map' | 'hybrid'
    const [optionsOpen, setOptionsOpen] = useState(true);
    const [eventsOpen, setEventsOpen] = useState(false);

    const [toggles, setToggles] = useState({
        activeEvents: true, earthquake: true, lightning: true, fireRisk: false, landslide: false,
    });

    const [adm0Geo, setAdm0Geo] = useState(null);
    const [adm3Geo, setAdm3Geo] = useState(null);
    const [adm3Loading, setAdm3Loading] = useState(false);
    const [buffer200Geo, setBuffer200Geo] = useState(null);

    const [rawEvents, setRawEvents] = useState([]);
    const [eventBuffers, setEventBuffers] = useState([]);
    const [quakes, setQuakes] = useState([]);
    const [lightningPoints, setLightningPoints] = useState([]);
    const [fireExtreme, setFireExtreme] = useState([]);
    const [landslideHigh, setLandslideHigh] = useState([]);
    const loadedKeys = useRef(new Set());

    // Hospitals (live API) and general infrastructure (static reprojected
    // geojson) — used only to count what falls inside a drawn polygon, not
    // rendered as their own map layer/toggle.
    const [hospitals, setHospitals] = useState([]);
    const [infrastructure, setInfrastructure] = useState([]);

    // Polygons drawn via PolygonDrawControl below the Home button. Each is
    // { id, geojson }; ids are sequential creation order (1, 2, 3, ...).
    const [polygons, setPolygons] = useState([]);
    const [downloadModalOpen, setDownloadModalOpen] = useState(false);

    // Loading flags feed the single Mainloader overlay below (same loader
    // used on the Forecast map, Dashboard/Home/Forecastmap.js). The three
    // default-on layers start true and flip false after their first fetch
    // completes (poll refreshes afterwards don't re-show the loader); the
    // model-data layers only show it for the one-shot fetch after being toggled on.
    const [loadingActiveEvents, setLoadingActiveEvents] = useState(true);
    const [loadingEarthquakes, setLoadingEarthquakes] = useState(true);
    const [loadingLightning, setLoadingLightning] = useState(true);
    const [fireRiskLoading, setFireRiskLoading] = useState(false);
    const [landslideLoading, setLandslideLoading] = useState(false);

    // Country outline: always on, small file.
    useEffect(() => {
        fetch("/DATA/OSMbased_adm0_boundary.json").then((r) => r.json()).then(setAdm0Geo).catch(() => {});
    }, []);

    // Earthquake "area of interest" — the real 200km buffer zone the actual
    // Earthquake/RecentEarthquake page draws (public/Earthquake/buffer_200km.geojson).
    // Small file, safe to load unconditionally since earthquake is default-on.
    useEffect(() => {
        fetch("/Earthquake/buffer_200km.geojson").then((r) => r.json()).then(setBuffer200Geo).catch(() => {});
    }, []);

    // Hospitals — same live endpoint as Homepage/Earthquake/HealthCenters.js.
    // Loaded once, unconditionally, since the polygon tool needs it regardless
    // of any layer toggle. Keeps the full record (not just lat/lon) so the
    // polygon download can export real detail, not just a count.
    useEffect(() => {
        fetch(`${BACKEND_URL}roadaccident/get_hospital_details/`)
            .then((r) => r.json())
            .then((data) => {
                const list = Array.isArray(data) ? data : [];
                setHospitals(
                    list
                        .filter((h) => h.latitude && h.longitude)
                        .map((h) => ({
                            lat: parseFloat(h.latitude),
                            lon: parseFloat(h.longitude),
                            name: h.name || "",
                            municipality: h.municipality || "",
                            facilityType: h.facility_type || "",
                            codeType: HOSPITAL_CODE_TYPE_MAP[String(h.code_type)]?.name || h.code_type || "",
                        }))
                );
            })
            .catch(() => {});
    }, []);

    // General infrastructure — same source file as Homepage/FireRisk/Infrastructure.js
    // (public/cityfire/infra_poly.geojson), reprojected from EPSG:32751 the
    // same way that page does it. Reduced to one centroid point per building
    // footprint (turf.centroid) since only in/out-of-polygon counting is
    // needed here, not the actual shapes. The "Hospital" sectorial category
    // is skipped — those are already covered by the live hospitals endpoint
    // above, and counting both would double-count the same real-world hospitals.
    useEffect(() => {
        proj4.defs("EPSG:32751", "+proj=utm +zone=51 +south +datum=WGS84 +units=m +no_defs");
        const reprojectCoordinates = (coordinates) =>
            coordinates.map((point) =>
                Array.isArray(point[0]) ? reprojectCoordinates(point) : proj4("EPSG:32751", "EPSG:4326", point)
            );

        fetch("/cityfire/infra_poly.geojson")
            .then((r) => r.json())
            .then((geojson) => {
                const points = [];
                (geojson.features || []).forEach((feature) => {
                    if (feature.properties?.SectorialS === "Hospital") return;
                    try {
                        const reprojected = {
                            ...feature,
                            geometry: { ...feature.geometry, coordinates: reprojectCoordinates(feature.geometry.coordinates) },
                        };
                        const c = turf.centroid(reprojected);
                        points.push({ lat: c.geometry.coordinates[1], lon: c.geometry.coordinates[0], type: feature.properties?.SectorialS || "Unknown" });
                    } catch (e) {
                        // Skip any feature with unparseable geometry.
                    }
                });
                setInfrastructure(points);
            })
            .catch(() => {});
    }, []);

    // Suco (adm3) boundary is 25MB — only fetched the first time a model-data
    // layer (fire risk / landslide) is switched on; used both to draw the
    // black suco outlines and to compute each suco's centroid for icons.
    const anyModelDataOn = toggles.fireRisk || toggles.landslide;
    useEffect(() => {
        if (anyModelDataOn && !adm3Geo && !adm3Loading) {
            setAdm3Loading(true);
            fetch("/DATA/OSMbased_adm3_boundary.json")
                .then((r) => r.json())
                .then(setAdm3Geo)
                .catch(() => {})
                .finally(() => setAdm3Loading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anyModelDataOn]);

    const adm3MetaByCode = useMemo(() => {
        const map = new Map();
        if (!adm3Geo) return map;
        adm3Geo.features.forEach((f) => {
            const code = f.properties?.ADM_Code;
            if (!code) return;
            let latlng = null;
            try {
                const c = turf.centroid(f);
                latlng = [c.geometry.coordinates[1], c.geometry.coordinates[0]];
            } catch (e) {
                latlng = null;
            }
            map.set(code, { latlng, adm1: f.properties?.ADM1, adm2: f.properties?.ADM2, adm3: f.properties?.ADM3 });
        });
        return map;
    }, [adm3Geo]);

    const loadActiveEvents = useCallback(async () => {
        // Verification-based visibility (regular users see verified-only,
        // Admin/Super Admin also see pending too) is resolved server-side
        // from request.user — the auth header is what makes that resolve to
        // the real caller instead of an anonymous one. 100m buffer-zone
        // grouping is computed entirely here on the frontend (buildEventBuffers),
        // not by the backend at all.
        const json = await safeFetchJson(`${BACKEND_URL}common/active-events/`, { headers: authHeaders() });
        const events = (json?.events || []).map((e) => ({
            id: e.id,
            bufferId: null,
            verificationStatus: e.verification_status,
            type: API_EVENT_LABEL_TO_TYPE[e.event] || "roadAccident",
            lat: e.lat, lon: e.lon,
            // Only set for single-point reports - a hand-drawn area or a
            // two-point stretch has lat/lon null and is rendered from this
            // instead (see common/views.py ActiveEventsView docstring).
            geometry: e.geometry || null,
            locationName: [e.sub_municipality, e.municipality].filter(Boolean).join(", ") || e.suco || "Unknown location",
            advisory: e.advisory,
            // report_group_count mirrors Report Approval's merge state (>1 =
            // other reports merged into this one there). `members` (each
            // duplicate's own id/lat/lon) is only ever populated server-side
            // for Admin/Super Admin - regular users just get the count.
            reportGroupCount: e.report_group_count || 1,
            members: e.members || [],
        }));
        setRawEvents(events);
        setEventBuffers(buildEventBuffers(events));
        setLoadingActiveEvents(false);
    }, []);

    // Past 24 hours from the current instant (event_datetime is UTC — parsed
    // via moment.utc() like every other earthquake view in this app, e.g.
    // Homepage/Earthquake/RecentEarthquake/EventList.js — a plain `new
    // Date(...)` would silently shift by the browser's local offset).
    const loadEarthquakes = useCallback(async () => {
        const json = await safeFetchJson(`${BACKEND_URL}EarthQuake/eq_events_all/`);
        const list = Array.isArray(json) ? json : json?.results || [];
        const cutoff = Date.now() - 24 * 3600 * 1000;
        const recent = list
            .filter((q) => moment.utc(q.event_datetime).valueOf() >= cutoff)
            .sort((a, b) => moment.utc(b.event_datetime).valueOf() - moment.utc(a.event_datetime).valueOf());
        setQuakes(recent);
        setLoadingEarthquakes(false);
    }, []);

    // Matches Nowcast.js's own fetchData(): current 5-min-aligned Dili slot,
    // "YYYY-MM-DD/HH:mm" datetime param, response is an array of named
    // buckets ({bucket, data, count}) rather than one flat point list.
    const loadLightning = useCallback(async () => {
        const slot = floorTo5Min(getTLNow());
        const dtParam = formatSlotForApi(slot);
        const json = await safeFetchJson(`${BACKEND_URL}lightning/nowcast/?datetime=${dtParam}`);
        const buckets = Array.isArray(json) ? json : [];
        const points = buckets.flatMap((b) => (b.data || []).map((p) => ({ ...p, bucket: b.bucket })));
        setLightningPoints(points);
        setLoadingLightning(false);
    }, []);

    // Fire risk: "current day" = the earliest forecast_date present in the
    // dataset — mirrors Homepage/FireRisk/Forecast/Forecast.js (forecastDates.sort()[0]).
    const loadFireRisk = useCallback(async () => {
        const json = await safeFetchJson(`${BACKEND_URL}firerisk/get-firerisk-data/`);
        const dataByAdm = json?.data || {};
        let minDate = null;
        Object.values(dataByAdm).forEach((records) => {
            (records || []).forEach((r) => {
                if (r?.forecast_date && (!minDate || r.forecast_date < minDate)) minDate = r.forecast_date;
            });
        });
        const extreme = [];
        Object.entries(dataByAdm).forEach(([admCode, records]) => {
            const rec = (records || []).find((r) => r.forecast_date === minDate);
            if (!rec) return;
            if (classifyFireAdvisory(rec.advisory) === "Extreme Risk") {
                extreme.push({ admCode, advisory: rec.advisory, adm1: rec.adm1, adm2: rec.adm2, adm3: rec.adm3, forecast_date: rec.forecast_date });
            }
        });
        setFireExtreme(extreme);
        setFireRiskLoading(false);
    }, []);

    // Landslide: "current day" = today in Asia/Dili — mirrors
    // Homepage/Landslide/Forecast/dateUtils.js getForecastDate(0).
    const loadLandslide = useCallback(async () => {
        const json = await safeFetchJson(`${BACKEND_URL}landslide/landslide-impact-risk-stats/current/`);
        const records = Array.isArray(json) ? json : [];
        const today = todayDiliStr();
        const grouped = {};
        records.forEach((r) => {
            if (!r?.adm_code) return;
            (grouped[r.adm_code] = grouped[r.adm_code] || []).push(r);
        });
        const high = [];
        Object.entries(grouped).forEach(([admCode, recs]) => {
            const rec = recs.find((r) => String(r.date || "").slice(0, 10) === today) || recs[recs.length - 1];
            if (!rec) return;
            if (classifyLandslideAdvisory(rec.advisory) === "High") {
                high.push({ admCode, advisory: rec.advisory, date: rec.date });
            }
        });
        setLandslideHigh(high);
        setLandslideLoading(false);
    }, []);

    // Active events load once on mount (default-on layer), then poll.
    useEffect(() => {
        loadActiveEvents();
        const poll = setInterval(loadActiveEvents, 120000);
        return () => clearInterval(poll);
    }, [loadActiveEvents]);

    // Lightning and Earthquake are also default-on, so they get the same
    // immediate-load + poll treatment as Active Events.
    useEffect(() => {
        loadLightning();
        loadedKeys.current.add("lightning");
        const poll = setInterval(loadLightning, 300000); // matches Nowcast.js's 5-minute auto-refresh
        return () => clearInterval(poll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadEarthquakes();
        loadedKeys.current.add("earthquake");
        const poll = setInterval(loadEarthquakes, 120000);
        return () => clearInterval(poll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Model-data layers stay lazy — only fetched the first time their toggle
    // is switched on (they default to off).
    useEffect(() => {
        if (toggles.fireRisk && !loadedKeys.current.has("fireRisk")) {
            loadedKeys.current.add("fireRisk");
            setFireRiskLoading(true);
            loadFireRisk();
        }
        if (toggles.landslide && !loadedKeys.current.has("landslide")) {
            loadedKeys.current.add("landslide");
            setLandslideLoading(true);
            loadLandslide();
        }
    }, [toggles, loadFireRisk, loadLandslide]);

    // Earthquake area-of-interest filter: only quakes physically inside the
    // 200km buffer zone count, on top of the 24h time filter already applied
    // in loadEarthquakes above.
    const quakesInAoi = useMemo(() => {
        if (!buffer200Geo) return [];
        const bufferFeature = buffer200Geo.features?.[0] || buffer200Geo;
        return quakes.filter((q) => {
            if (q.latitude === undefined || q.latitude === null || q.longitude === undefined || q.longitude === null) return false;
            try {
                return turf.booleanPointInPolygon(turf.point([q.longitude, q.latitude]), bufferFeature);
            } catch (e) {
                return false;
            }
        });
    }, [quakes, buffer200Geo]);

    const toggleLayer = (key) => setToggles((prev) => ({ ...prev, [key]: !prev[key] }));

    // Lightning can be thousands of raw points — the drawer summarizes them
    // one row per non-empty recency bucket instead of one row per point.
    const lightningBucketSummary = useMemo(() => {
        const counts = {};
        lightningPoints.forEach((p) => {
            counts[p.bucket] = (counts[p.bucket] || 0) + 1;
        });
        return Object.entries(LIGHTNING_BUCKET_META)
            .map(([bucket, meta]) => ({ bucket, count: counts[bucket] || 0, ...meta }))
            .filter((b) => b.count > 0);
    }, [lightningPoints]);

    // Drawer content mirrors whatever is actually drawn on the map: only
    // layers currently toggled on contribute rows, in the same order they're
    // listed in the View options panel (Live points, then Model Data).
    const drawerItems = useMemo(() => {
        const items = [];

        if (toggles.activeEvents) {
            // Buffered reports collapse into one row per zone (same grouping
            // shown on the map) instead of one row per report; everything
            // else lists individually as before.
            const eventsById = new Map(rawEvents.map((e) => [e.id, e]));
            eventBuffers.forEach((b) => {
                const firstMember = eventsById.get(b.eventIds[0]);
                items.push({
                    key: b.id,
                    color: b.hasPending ? "#f59e0b" : "#dc3545",
                    Icon: CircleDashed,
                    title: `Buffer Zone (${b.reportCount} reports)`,
                    subtitle: `${b.modules.join(", ")}${firstMember ? ` • ${firstMember.locationName}` : ""}`,
                    advisory: b.hasPending ? "Includes pending report(s)" : null,
                });
            });
            rawEvents.filter((e) => !e.bufferId).forEach((e, idx) => {
                const meta = EVENT_TYPE_META[e.type] || EVENT_TYPE_META.roadAccident;
                items.push({
                    key: `ae-${idx}`,
                    color: meta.color,
                    Icon: meta.Icon,
                    title: meta.label,
                    subtitle: e.locationName,
                    advisory: e.advisory,
                });
            });
        }

        if (toggles.earthquake) {
            quakesInAoi.forEach((q, idx) => {
                items.push({
                    key: `eq-${idx}`,
                    color: earthquakeColor(Number(q.magnitude || 0)),
                    Icon: Waves,
                    title: q.region || q.place || "Earthquake",
                    subtitle: `Magnitude ${q.magnitude ?? "N/A"} • Depth ${q.depth ?? "N/A"} km`,
                });
            });
        }

        if (toggles.lightning) {
            lightningBucketSummary.forEach((b) => {
                items.push({
                    key: `lt-${b.bucket}`,
                    color: b.color,
                    Icon: Zap,
                    title: "Lightning Observations",
                    subtitle: `${b.label} • ${b.count} point${b.count === 1 ? "" : "s"}`,
                });
            });
        }

        if (toggles.fireRisk) {
            fireExtreme.forEach((s, idx) => {
                const meta = adm3MetaByCode.get(s.admCode);
                const name = s.adm3 || meta?.adm3 || s.admCode;
                const municipality = [s.adm2 || meta?.adm2, s.adm1 || meta?.adm1].filter(Boolean).join(", ");
                items.push({
                    key: `fr-${idx}`,
                    color: EVENT_TYPE_META.fire.color,
                    Icon: Flame,
                    title: name,
                    subtitle: municipality || "Extreme Fire Risk",
                    advisory: s.advisory || "Extreme Risk",
                });
            });
        }

        if (toggles.landslide) {
            landslideHigh.forEach((s, idx) => {
                const meta = adm3MetaByCode.get(s.admCode);
                const name = meta?.adm3 || s.admCode;
                const municipality = [meta?.adm2, meta?.adm1].filter(Boolean).join(", ");
                items.push({
                    key: `ls-${idx}`,
                    color: EVENT_TYPE_META.landslide.color,
                    Icon: Mountain,
                    title: name,
                    subtitle: municipality || "High Landslide Risk",
                    advisory: s.advisory || "High Risk",
                });
            });
        }

        return items;
    }, [toggles, rawEvents, eventBuffers, quakesInAoi, lightningBucketSummary, fireExtreme, landslideHigh, adm3MetaByCode]);

    // Hospitals/infrastructure records inside each drawn polygon — same
    // turf.booleanPointInPolygon approach already used above for the
    // earthquake AOI filter, just run per polygon against the two static
    // point sets instead of a single fixed buffer. Keeps the full matched
    // records (not just a count) so the download can export real detail.
    const polygonMatches = useMemo(() => {
        return polygons.map((p) => {
            const filterInside = (points) =>
                points.filter((pt) => {
                    try {
                        return turf.booleanPointInPolygon(turf.point([pt.lon, pt.lat]), p.geojson);
                    } catch (e) {
                        return false;
                    }
                });
            return {
                id: p.id,
                hospitals: filterInside(hospitals),
                infrastructure: filterInside(infrastructure),
            };
        });
    }, [polygons, hospitals, infrastructure]);

    // If a suco has more than one model-data marker (e.g. Extreme fire risk
    // AND High landslide risk on the same day), spread them evenly around
    // the centroid instead of stacking them on the exact same point.
    const forecastOffsets = useMemo(() => {
        const kindsByAdmCode = new Map();
        fireExtreme.forEach((s) => {
            if (!kindsByAdmCode.has(s.admCode)) kindsByAdmCode.set(s.admCode, []);
            kindsByAdmCode.get(s.admCode).push("fire");
        });
        landslideHigh.forEach((s) => {
            if (!kindsByAdmCode.has(s.admCode)) kindsByAdmCode.set(s.admCode, []);
            kindsByAdmCode.get(s.admCode).push("landslide");
        });

        const offsetMap = new Map();
        const RADIUS_DEG = 0.006; // roughly 650m — enough to visually separate two icons at suco scale
        kindsByAdmCode.forEach((kinds, admCode) => {
            const n = kinds.length;
            kinds.forEach((kind, i) => {
                if (n <= 1) {
                    offsetMap.set(`${admCode}:${kind}`, [0, 0]);
                    return;
                }
                const angle = (2 * Math.PI * i) / n;
                offsetMap.set(`${admCode}:${kind}`, [RADIUS_DEG * Math.sin(angle), RADIUS_DEG * Math.cos(angle)]);
            });
        });
        return offsetMap;
    }, [fireExtreme, landslideHigh]);

    // "No active events" only means something for the live-points layers; once
    // a model-data (forecast) layer is on, its icons are the content instead.
    const anyLivePointsOn = toggles.activeEvents || toggles.earthquake || toggles.lightning;
    const hasAnyLiveData =
        (toggles.activeEvents && rawEvents.length > 0) ||
        (toggles.earthquake && quakesInAoi.length > 0) ||
        (toggles.lightning && lightningPoints.length > 0);
    const showNoActiveEventsText = anyLivePointsOn && !hasAnyLiveData && !anyModelDataOn;

    // Same loader used on the Forecast map (Dashboard/Home/Forecastmap.js):
    // true while any layer's data is still being fetched for the first time.
    const showMapLoader =
        loadingActiveEvents || loadingEarthquakes || loadingLightning || adm3Loading || fireRiskLoading || landslideLoading;

    return (
        <div className="aeh_page">
            <div className="aeh_map-shell">
                <MapContainer center={MAP_CENTER} zoom={DEFAULT_ZOOM} minZoom={7} className="aeh_map" attributionControl={false} preferCanvas>
                    <MapHomeControl center={MAP_CENTER} zoom={DEFAULT_ZOOM} />
                    <PolygonDrawControl
                        hasPolygons={polygons.length > 0}
                        onCreated={(geojson) => setPolygons((prev) => [...prev, { id: prev.length + 1, geojson }])}
                        onClear={() => setPolygons([])}
                    />
                    <MapInfoControl
                        title="Active Events Hub"
                        width="340px"
                        text={
                            <>
                                Toggle live points (Active Events, Earthquake, Lightning) and model data (Fire Risk, Landslide) from View options, top-right — the Events drawer (arrow, left edge) lists everything currently on the map.
                                <br /><br />
                                Click the polygon tool below the Home button to draw polygons — finishing one immediately arms the next, so you can draw several in a row without clicking again; click the polygon tool once more to stop. Matching Hospitals and Infrastructure counts show up bottom-left for each polygon.
                                <br /><br />
                                Once a polygon exists, Download Data (next to the Map/Hybrid toggle) exports the counts — per polygon or combined into one file, under a name you choose.
                            </>
                        }
                    />
                    {basemap === "map" ? (
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    ) : (
                        <>
                            <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
                            <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}" />
                        </>
                    )}

                    <AdmContextOutline geo={adm0Geo} weight={2} />
                    {anyModelDataOn && adm3Geo && <AdmContextOutline geo={adm3Geo} weight={0.6} />}

                    {toggles.activeEvents && <ActiveEventsMarkersLayer events={rawEvents} buffers={eventBuffers} onRefresh={loadActiveEvents} />}
                    {toggles.earthquake && <EarthquakeMarkersLayer quakes={quakesInAoi} />}
                    {toggles.lightning && <LightningMarkersLayer points={lightningPoints} />}

                    {toggles.fireRisk && (
                        <ForecastIconLayer sucos={fireExtreme} metaByCode={adm3MetaByCode} gifUrl="/Dashboard/Legends/fire.gif" riskLabel="Extreme (Fire)" kind="fire" offsets={forecastOffsets} />
                    )}
                    {toggles.landslide && (
                        <ForecastIconLayer sucos={landslideHigh} metaByCode={adm3MetaByCode} gifUrl="/Dashboard/Legends/landslide.gif" riskLabel="High (Landslide)" kind="landslide" offsets={forecastOffsets} />
                    )}
                </MapContainer>

                {showMapLoader && (
                    <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
                        <Mainloader />
                    </div>
                )}

                {!showMapLoader && showNoActiveEventsText && (
                    <div className="aeh_empty-badge">No active events</div>
                )}

                {/* Basemap segmented control + polygon download */}
                <div className="aeh_top-toolbar">
                    <div className="aeh_basemap-toggle">
                        <button className={basemap === "map" ? "active" : ""} onClick={() => setBasemap("map")}>Map</button>
                        <button className={basemap === "hybrid" ? "active" : ""} onClick={() => setBasemap("hybrid")}>Hybrid</button>
                    </div>
                    {polygons.length > 0 && (
                        <button className="aeh_download-btn" onClick={() => setDownloadModalOpen(true)}>
                            <Download size={14} /> Download Data
                        </button>
                    )}
                </div>

                {/* Hospitals/Infrastructure counts per drawn polygon */}
                {polygons.length > 0 && (
                    <div className="aeh_polygon-counts">
                        {polygonMatches.map((pm) => (
                            <div className="aeh_polygon-count-row" key={pm.id}>
                                <div className="aeh_polygon-count-label">Polygon {pm.id}</div>
                                <div className="aeh_polygon-count-stats">
                                    <span>Hospitals: <strong>{pm.hospitals.length}</strong></span>
                                    <span>Infrastructure: <strong>{pm.infrastructure.length}</strong></span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* View options panel (right) */}
                {optionsOpen ? (
                    <div className="aeh_options-panel">
                        <div className="aeh_options-header">
                            <span><Layers size={15} /> View options</span>
                            <button className="aeh_icon-btn" onClick={() => setOptionsOpen(false)} aria-label="Close view options"><X size={16} /></button>
                        </div>

                        <div className="aeh_options-section-title">Live points</div>
                        <LayerRow label="Active Events" color="#dc3545" checked={toggles.activeEvents} onChange={() => toggleLayer("activeEvents")} />
                        <LayerRow label="Earthquake — Last 24h (200km AOI)" color="#fd7e14" checked={toggles.earthquake} onChange={() => toggleLayer("earthquake")} />
                        <LayerRow label="Lightning Observations" color="#ef4444" checked={toggles.lightning} onChange={() => toggleLayer("lightning")} />
                        {toggles.lightning && (
                            <div className="aeh_hint">Colored by recency: red (last 5 min), orange (10–5 min), yellow (15–10 min), light yellow (30–15 min), grey (up to 24h).</div>
                        )}

                        <div className="aeh_options-section-title">Model Data</div>
                        <LayerRow label="Fire Risk Forecast" color="#dc2626" checked={toggles.fireRisk} onChange={() => toggleLayer("fireRisk")} />
                        <LayerRow label="Landslide Forecast" color="#6f42c1" checked={toggles.landslide} onChange={() => toggleLayer("landslide")} />
                        <div className="aeh_hint">Today's model output only — fire icons mark Extreme risk sucos, landslide icons mark High risk sucos.</div>
                        {adm3Loading && <div className="aeh_hint">Loading suco boundaries…</div>}
                    </div>
                ) : (
                    <button className="aeh_options-fab" onClick={() => setOptionsOpen(true)}>
                        <Layers size={16} /> View options
                    </button>
                )}

                {/* Active events drawer (left) */}
                <button
                    className={`aeh_events-tab ${eventsOpen ? "open" : ""}`}
                    onClick={() => setEventsOpen((v) => !v)}
                    aria-label={eventsOpen ? "Collapse active events" : "Expand active events"}
                >
                    {eventsOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
                </button>
                {eventsOpen && (
                    <div className="aeh_events-drawer">
                        <div className="aeh_options-header">
                            <span>Events ({drawerItems.length})</span>
                        </div>
                        <div className="aeh_events-list">
                            {drawerItems.length === 0 && <div className="aeh_hint" style={{ padding: "14px 16px" }}>No active events right now.</div>}
                            {drawerItems.map((item) => (
                                <div className="aeh_event-item" key={item.key}>
                                    <div className="aeh_event-item-icon" style={{ backgroundColor: `${item.color}1a`, color: item.color }}>
                                        <item.Icon size={15} />
                                    </div>
                                    <div>
                                        <div className="aeh_event-item-title">{item.title}</div>
                                        <div className="aeh_event-item-sub">{item.subtitle}</div>
                                        {item.advisory && <div className="aeh_event-item-advisory">{item.advisory}</div>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <PolygonDownloadModal
                    show={downloadModalOpen}
                    onHide={() => setDownloadModalOpen(false)}
                    polygonMatches={polygonMatches}
                />
            </div>
        </div>
    );
};

const LayerRow = ({ label, color, checked, onChange }) => (
    <label className="aeh_layer-row">
        <span className="aeh_layer-label">
            <span className="aeh_layer-dot" style={{ backgroundColor: color }} />
            {label}
        </span>
        <span className={`aeh_switch ${checked ? "on" : ""}`} onClick={onChange} role="switch" aria-checked={checked}>
            <span className="aeh_switch-knob" />
        </span>
    </label>
);

export default ActiveEventsHub;
